<template>
  <v-layout column fill-height fill-width>
    <v-tabs v-model="activeTab">
      <v-tab ripple>Data table</v-tab>
      <v-tab-item>
        <v-flex xs12>
          <v-card>
            <v-card-title>
              <h1>Analyzed Data</h1>
              <v-chip color="blue" class="white--text font-weight-bold" label>{{ `${startDate.replace(/-/g, '/')} -
                ${endDate.replace(/-/g, '/')}` }}
              </v-chip>
              <v-spacer></v-spacer>
              <v-flex xs12 lg2>
                <v-text-field
                  v-model="search"
                  append-icon="search"
                  label="Search"
                  single-line
                  hide-details
                  outline
                ></v-text-field>
              </v-flex>
              <v-dialog v-model="dialog" persistent max-width="800px">
                <v-btn slot="activator" color="primary">Settings</v-btn>
                <v-card>
                  <v-card-title>
                    <h2>Lookup Settings</h2>
                  </v-card-title>
                  <v-card-text>
                    <v-form v-model="formValid">
                      <v-container grid-list-md>
                        <v-layout row wrap>
                          <v-flex xs12 md6>
                            <v-select
                              v-model="status"
                              :items="statuses"
                              label="Status"
                              outline
                            ></v-select>
                          </v-flex>
                          <v-flex xs12 md6>
                            <v-select
                              v-model="statusRule"
                              :items="['Get only this status', 'Get all statuses after']"
                              label="Status rules"
                              outline
                            ></v-select>
                          </v-flex>
                          <v-flex xs12 md6>
                            <v-menu
                              v-model="menu.startdate"
                              :nudge-right="40"
                              lazy
                              transition="scale-transition"
                              offset-y
                              full-width
                              max-width="290px"
                              min-width="290px"
                            >
                              <v-text-field
                                v-model="startDate"
                                slot="activator"
                                label="Start Date"
                                :rules="rules.startDate"
                                readonly
                                outline
                              ></v-text-field>
                              <v-date-picker v-model="startDate" no-title
                                             @input="menu.startdate = false"></v-date-picker>
                            </v-menu>
                          </v-flex>
                          <v-flex xs12 md6>
                            <v-menu
                              v-model="menu.enddate"
                              :nudge-right="40"
                              lazy
                              transition="scale-transition"
                              offset-y
                              full-width
                              max-width="290px"
                              min-width="290px"
                            >
                              <v-text-field
                                v-model="endDate"
                                slot="activator"
                                label="End Date"
                                :rules="rules.endDate"
                                readonly
                                outline
                              ></v-text-field>
                              <v-date-picker v-model="endDate" no-title @input="menu.enddate = false"></v-date-picker>
                            </v-menu>
                          </v-flex>
                        </v-layout>
                      </v-container>
                    </v-form>
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn :disabled="!formValid" color="blue darken-1" flat @click.native="updateSearch">Close</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-card-title>
            <hr>
            <v-card-text>
              <v-data-table
                :headers="headers1"
                :items="analyzedData"
                class="elevation-1"
                hide-actions
                :search="search"
                :loading="loading"
                :expand="expand"
                item-key="job_id"
              >
                <template slot="items" slot-scope="props">
                  <tr
                    v-bind:class="{'border_bottom': props.item.screener_name.includes('TOTAL')}"
                    @click="expandRow(props)"
                  >
                    <td>{{ props.item.screener_name }}</td>
                    <td>{{ (props.item.job_id.length > 5) ? props.item.job_id.slice(5) : `${props.item.job_id} jobs`
                      }}
                    </td>
                    <td>{{ props.item.status_displayname }}</td>
                    <td>{{ props.item.camid }}</td>
                    <td>{{ props.item.isdigital }}</td>
                    <td>{{ props.item.screened }}</td>
                    <td>{{ props.item.xcount }}</td>
                    <td>{{ props.item.screened - props.item.xcount }}</td>
                    <td>{{ (100*(props.item.xcount / props.item.screened)).toFixed(1) }}%</td>
                    <td>{{ props.item.dilate }}</td>
                    <td>{{ props.item.lookoff }}</td>
                    <td>{{ props.item.force_eye }}</td>
                    <td>{{ props.item.focus }}</td>
                  </tr>
                </template>
                <template slot="expand" slot-scope="props">
                  <v-card flat>
                    <v-container v-if="selectedJobInfo" fluid grid-list-md>
                      <v-layout row wrap>
                        <v-flex xs12 md4>
                          <v-text-field
                            label="Job Date"
                            :value="formatDate(selectedJobInfo.date)"
                            readonly
                          ></v-text-field>
                        </v-flex>
                        <v-flex xs12 md4>
                          <v-text-field
                            label="Finalized Date"
                            :value="formatDate(selectedJobInfo.finalized_date)"
                            readonly
                          ></v-text-field>
                        </v-flex>
                      </v-layout>
                      <v-flex xs12 md4>
                        <v-text-field
                          label="School"
                          :value="JSON.parse(selectedJobInfo.meta).addr2"
                          readonly
                        ></v-text-field>
                      </v-flex>
                    </v-container>
                    <div v-else class="text-xs-center">
                      <v-progress-circular indeterminate color="blue"></v-progress-circular>
                    </div>
                  </v-card>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-tab-item>
      <v-tab ripple>Graph</v-tab>
      <v-tab-item>
        <v-flex>
          <BacklogGraph ref="graph" :finalDate="finalDate"></BacklogGraph>
        </v-flex>
      </v-tab-item>
      <v-spacer></v-spacer>
      <v-btn v-if="activeTab === 0" @click="generateTablePDF()">Export</v-btn>
      <v-btn v-if="activeTab === 1" @click="$refs.graph.generateGraphPDF()">Export</v-btn>
    </v-tabs>
  </v-layout>
</template>

<script>
  import moment from 'moment'
  import BacklogGraph from '../../components/graphs/BacklogGraph'
  import pdfMake from 'pdfmake'

  export default {
    name: 'Dashboard',
    components: { BacklogGraph },
    data: () => ({
      activeTab: null,

      search: '',
      headers1: [
        { text: 'Screener', value: 'screener_name' },
        { text: 'Job ID', value: 'job_id' },
        { text: 'Status', value: 'status_displayname' },
        { text: 'Unit', value: 'camid' },
        { text: 'Type', value: 'isdigital' },
        { text: 'Screened', value: 'screened' },
        { text: 'X Count', value: 'xcount' },
        { text: 'Analyzed Count', value: '' },
        { text: '% X\'s', value: '' },
        { text: 'Dilate', value: 'dilate' },
        { text: 'Lookoff', value: 'lookoff' },
        { text: 'Force', value: 'force_eye' },
        { text: 'Focus', value: 'focus' },
      ],
      analyzedData: [],

      totals: [],

      status: 'Done',
      statusRule: 'Get only this status',
      statuses: [],

      selectedJobInfo: null,

      loading: false,
      expand: false,
      dialog: false,
      formValid: true,
      startDate: '',
      endDate: '',
      finalDate: null, // date time
      rules: {
        startDate: [
          v => !!v || 'Start date is required.'
        ],
        endDate: [
          v => !!v || 'End date is required.'
        ],
      },
      menu: {
        startdate: false,
        enddate: false,
      }
    }),
    watch: {
      activeTab (newVal) {
        if (newVal === 1)
          this.$refs.graph.loadGraph()
      }
    },
    methods: {
      updateSearch () {
        this.dialog = false
        this.getData()
      },
      expandRow (props) {
        // only open actual job ids
        if (!isNaN(props.item.job_id)) return
        props.expanded = !props.expanded
        // don't grab data on expand false
        if (!props.expanded) return
        // reset data
        this.selectedJobInfo = null
        // get job info
        // https://vrc.api.maxis.io/v1/job/job_id
        this.$http.get(`job/${props.item.job_id}`, {
          headers: { 'Authorization': localStorage.getItem('apiKey') }
        }).then(response => {
          this.selectedJobInfo = response.data[0]
        }).catch(error => {

        })
      },
      formatDate (date) {
        date = new Date(date)
        return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
      },
      populateStatuses () {
        this.$http.get('/jobs/status_priority', {
          headers: { 'Authorization': localStorage.getItem('apiKey') }
        }).then(response => {
          this.statuses = response.data.map(obj => {return obj.displayname})
        }).catch(error => {

        })
      },
      generateTablePDF () {
        const start = moment(this.startDate)
        const end = moment(this.endDate)

        const pdfTableFontSize = 8

        // create table header
        let tableHeader = ['Screener', 'Loc\'s', '# Scrnd', '# X\'s', '# Anal\'d', '% X\'s', 'Dilate', 'Lookoff', 'Force', 'Focus', 'Total']
        tableHeader = tableHeader.map(e => {return { text: e, fontSize: pdfTableFontSize }})

        // create table data
        let table = this.totals.map(e => {
          return [
            { text: e.screener_name.replace(' (TOTAL)', ''), fontSize: pdfTableFontSize },
            { text: e.job_id, fontSize: pdfTableFontSize },
            { text: e.screened, fontSize: pdfTableFontSize },
            { text: e.xcount, fontSize: pdfTableFontSize },
            { text: e.screened - e.xcount, fontSize: pdfTableFontSize },
            { text: `${(100 * (e.xcount / e.screened)).toFixed(2)}%`, fontSize: pdfTableFontSize },
            { text: e.dilate, fontSize: pdfTableFontSize },
            { text: e.lookoff, fontSize: pdfTableFontSize },
            { text: e.force_eye, fontSize: pdfTableFontSize },
            { text: e.focus, fontSize: pdfTableFontSize },
            { text: e.dilate + e.lookoff + e.force_eye + e.focus, fontSize: pdfTableFontSize }
          ]
        })
        // sort table by x count
        table = table.sort(function (a, b) {
          return a[3].text - b[3].text
        })

        // add table header to table
        table.unshift(new Array(11).fill({ text: ' ', fontSize: pdfTableFontSize }))
        table.unshift(tableHeader)

        // pdf options
        const dd = {
          pageSize: 'A5',
          pageOrientation: 'landscape',
          content: [
            {
              columns: [
                { width: '*', text: '' },
                {
                  width: 'auto',
                  table: {
                    body: [['Summary Ranked']]
                  }
                },
                { width: '*', text: '' },
              ]
            },
            {
              text: [
                {
                  text: `Jobs completed ${start.format('MM/DD')} - ${end.format('MM/DD/YY')}`,
                  fontSize: 9,
                  color: 'blue'
                },
              ]
            },
            {
              layout: 'noBorders',
              table: {
                style: 'table',
                body: table
              }
            },
          ],
          styles: {
            table: {
              fontSize: 6
            }
          }
        }
        // create pdf
        pdfMake.createPdf(dd).download(`jobcompleted_${start.format('MM-DD-YY')}_${end.format('MM-DD-YY')}`)
      },
      getData () {
        this.$http.get(`/jobs/xsummary/${this.startDate}/${this.endDate}?status=${encodeURI(this.status)}&greaterthan=${(this.statusRule !== 'Get only this status')}`, {
          headers: { 'Authorization': localStorage.getItem('apiKey') }
        }).then(response => {
          this.analyzedData = response.data

          // get totals
          const totals = {}
          this.analyzedData.forEach(each => {
            if (!totals[each.screener_name]) {
              totals[each.screener_name] = {
                locations: 1,
                xcount: each.xcount,
                screened: each.screened,
                dilate: each.dilate,
                lookoff: each.lookoff,
                force_eye: each.force_eye,
                focus: each.focus,
              }
            } else {
              totals[each.screener_name].locations += 1
              totals[each.screener_name].xcount += each.xcount
              totals[each.screener_name].screened += each.screened
              totals[each.screener_name].dilate += each.dilate
              totals[each.screener_name].lookoff += each.lookoff
              totals[each.screener_name].force_eye += each.force_eye
              totals[each.screener_name].focus += each.focus
            }
          })

          // create a list of totals
          this.totals = Object.keys(totals).map(key => {
            return {
              screener_name: `${key} (TOTAL)`,
              job_id: totals[key].locations,
              xcount: totals[key].xcount,
              screened: totals[key].screened,
              dilate: totals[key].dilate,
              lookoff: totals[key].lookoff,
              force_eye: totals[key].force_eye,
              focus: totals[key].focus,
            }
          })

          // merge totals with main list
          this.analyzedData = this.analyzedData.concat(this.totals)
        }).catch(error => {

        })
      }
    },
    async mounted () {
      this.loading = true

      // set date range
      const currDate = new Date()
      // if (currDate.getMonth() + 1 < 7) {
      //   this.startDate = `${currDate.getFullYear() - 1}-7-1`
      //   this.endDate = `${currDate.getFullYear()}-7-1`
      // } else {
      //   this.startDate = `${currDate.getFullYear()}-7-1`
      //   this.endDate = `${currDate.getFullYear() + 1}-7-1`
      // }

      const start = new Date()

      // set to Monday of this week
      start.setDate(start.getDate() - (start.getDay() + 6) % 7)
      // set to previous Monday
      start.setDate(start.getDate() - 7)
      // get end date at end of that week
      const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6)
      this.finalDate = end

      // get last week
      this.startDate = `${start.getFullYear()}-${start.getMonth() + 1}-${start.getDate()}`
      this.endDate = `${end.getFullYear()}-${end.getMonth() + 1}-${end.getDate()}`

      // get data
      await this.populateStatuses()
      await this.getData()
      this.loading = false
    }
  }
</script>

<style scoped>
  tr.border_bottom td {
    border-bottom: 3pt solid black;
  }
</style>



// WEBPACK FOOTER //
// src/pages/admin/Dashboard.vue